import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Zap, Volume2, Users, Briefcase, Battery, Sofa } from "lucide-react";

// Hero photos of the live Sarathi-X car. We rotate through them so visitors
// see both the streetside (tree canopy) feel AND the Sarathi-X livery side.
const FLEET_PHOTOS = [
  {
    src: "https://customer-assets.emergentagent.com/job_ev-mobility-2/artifacts/sgpg9oec_SarathiX%20Static%20%281%29.JPG",
    alt: "Sarathi-X MG Windsor EV (KA01AT8260) parked on a tree-lined Bengaluru street",
    // Photo is roughly square — push the visible window down so we crop the
    // top canopy rather than the car roof.
    objectPosition: "center 70%",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_ev-mobility-2/artifacts/uamqnu6s_SarathiX%20Static%20%283%29.jpg",
    alt: "Sarathi-X MG Windsor EV side profile with Sarathi-X branding on the door",
    objectPosition: "center center",
  },
];

const FleetCarousel = ({ rounded = "rounded-3xl" }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % FLEET_PHOTOS.length);
    }, 5000);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <div
      className={`bg-sarathi-background border border-white/10 ${rounded} overflow-hidden hover:border-sarathi-primary/50 transition-all duration-300`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="fleet-carousel"
    >
      <div className="aspect-[16/10] relative">
        {FLEET_PHOTOS.map((p, i) => (
          <img
            key={p.src}
            src={p.src}
            alt={p.alt}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out ${
              i === idx ? "opacity-100" : "opacity-0"
            }`}
            style={{ objectPosition: p.objectPosition }}
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "auto"}
          />
        ))}

        {/* Dots */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {FLEET_PHOTOS.map((p, i) => (
            <button
              key={p.src}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`Show photo ${i + 1}`}
              data-testid={`fleet-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === idx
                  ? "w-6 bg-white"
                  : "w-1.5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const FleetSection = () => {
  const features = [
    { icon: Battery, title: "~300 km Range", description: "38 kWh battery for worry-free journeys" },
    { icon: Sofa, title: "Aero-Lounge Seats", description: "135° reclining rear seats for comfort" },
    { icon: Zap, title: "Zero Emissions", description: "100% electric" },
    { icon: Volume2, title: "Silent Cabin", description: "Quieter than you'd expect" },
    { icon: Users, title: "4 Passengers", description: "Max 4 passengers with luggage" },
    { icon: Briefcase, title: "605L Boot", description: "2–3 large check-in bags and 2 cabin bags" },
  ];

  return (
    <section id="fleet" className="py-16 sm:py-20 bg-sarathi-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="text-sarathi-primary font-semibold text-xs tracking-widest uppercase">
            Our Fleet
          </span>
          <h2 className="font-outfit text-2xl sm:text-3xl font-bold tracking-tight text-white mt-3 mb-3">
            MG Windsor EV
          </h2>
          <p className="text-sarathi-text-secondary text-sm max-w-xl mx-auto">
            Chosen for comfort, reliability, and a smooth airport journey.
          </p>
        </motion.div>

        {/* Desktop: Image left + 2 tall cards right. Mobile: Image on top, uniform grid below */}
        <div className="hidden lg:grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Main Image — Desktop */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-8"
          >
            <FleetCarousel rounded="rounded-3xl" />
          </motion.div>

          {/* Side Cards — Desktop */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            {features.slice(0, 2).map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-sarathi-background border border-white/10 rounded-2xl p-6 hover:border-sarathi-primary/50 transition-all duration-300 flex-1"
              >
                <div className="w-12 h-12 bg-sarathi-primary/20 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-sarathi-primary" />
                </div>
                <h4 className="font-outfit text-xl font-semibold text-white mb-2">{feature.title}</h4>
                <p className="text-sarathi-text-secondary text-sm whitespace-pre-line">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Desktop: Bottom feature grid (4 cols) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="hidden lg:grid lg:grid-cols-4 gap-6 mt-10"
        >
          {features.slice(2).map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-sarathi-background border border-white/10 rounded-2xl p-6 hover:border-sarathi-primary/30 transition-all"
            >
              <div className="w-12 h-12 bg-sarathi-primary/10 rounded-xl flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-sarathi-primary" />
              </div>
              <h5 className="font-outfit font-semibold text-white text-lg mb-1">{feature.title}</h5>
              <p className="text-sarathi-text-muted text-sm whitespace-pre-line">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Mobile: Image + uniform grid */}
        <div className="lg:hidden">
          {/* Car Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6"
          >
            <FleetCarousel rounded="rounded-2xl" />
          </motion.div>

          {/* Uniform 2-col grid — all cards same size */}
          <div className="grid grid-cols-2 gap-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-sarathi-background border border-white/10 rounded-xl p-4 hover:border-sarathi-primary/30 transition-all"
              >
                <div className="w-10 h-10 bg-sarathi-primary/10 rounded-lg flex items-center justify-center mb-3">
                  <feature.icon className="w-5 h-5 text-sarathi-primary" />
                </div>
                <h5 className="font-outfit font-semibold text-white text-sm mb-1">{feature.title}</h5>
                <p className="text-sarathi-text-muted text-xs whitespace-pre-line">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FleetSection;
